import { useState, useRef, useCallback } from 'react'
import { UNTAGGED_FILTER } from '@/hooks/UsePlaybookLabels'
import { LabelCreatePopover } from './LabelCreatePopover'
import { LabelFilterPopover } from './LabelFilterPopover'
import type { PlaybookLabel } from '@/types/Label.types'
import './label-filter-bar.css'

interface LabelFilterBarProps {
	labels: PlaybookLabel[]
	counts: Record<string, number>
	totalCount: number
	untaggedCount: number
	activeFilter: string | null
	activeFilterIds?: string[]
	/** When true, an external view (e.g. Trash) is active — suppresses "All" pill highlight */
	isExternalViewActive?: boolean
	onFilterChange: (filter: string | null) => void
	onFilterIdsChange?: (filterIds: string[]) => void
	onCreateLabel: (data: { name: string; color: string }) => Promise<void>
	onReorder: (labelIds: string[]) => Promise<void>
}

export function LabelFilterBar({
	labels,
	counts,
	totalCount,
	untaggedCount,
	activeFilter,
	activeFilterIds = [],
	isExternalViewActive = false,
	onFilterChange,
	onFilterIdsChange,
	onCreateLabel,
	onReorder,
}: LabelFilterBarProps) {
	const [showCreate, setShowCreate] = useState(false)
	const [showFilter, setShowFilter] = useState(false)
	const [draggedId, setDraggedId] = useState<string | null>(null)
	const createBtnRef = useRef<HTMLButtonElement>(null)
	const filterBtnRef = useRef<HTMLButtonElement>(null)

	const hasTaggedItems = labels.length > 0 && Object.values(counts).some((c) => c > 0)
	const showUntagged = hasTaggedItems && untaggedCount > 0
	const isMultiFilter = activeFilterIds.length > 0

	const handleDragStart = (labelId: string) => setDraggedId(labelId)

	const handleDragOver = (e: React.DragEvent, targetId: string) => {
		e.preventDefault()
		if (!draggedId || draggedId === targetId) return
		const currentOrder = labels.map((l) => l.id)
		const fromIdx = currentOrder.indexOf(draggedId)
		const toIdx = currentOrder.indexOf(targetId)
		if (fromIdx === -1 || toIdx === -1) return
		currentOrder.splice(fromIdx, 1)
		currentOrder.splice(toIdx, 0, draggedId)
		onReorder(currentOrder)
	}

	const handleDragEnd = () => setDraggedId(null)

	const handleCloseCreate = useCallback(() => setShowCreate(false), [])
	const handleCloseFilter = useCallback(() => setShowFilter(false), [])

	return (
		<div className="label-filter-bar" role="toolbar" aria-label="Label filters">
			<div className="label-filter-bar-scroll">
				{/* All pill */}
				<button
					className="label-filter-pill"
					data-active={!activeFilter && !isMultiFilter && !isExternalViewActive}
					aria-pressed={!activeFilter && !isMultiFilter && !isExternalViewActive}
					onClick={() => onFilterChange(null)}
				>
					All
					<span className="label-filter-pill-count">{totalCount}</span>
				</button>

				{/* Label pills */}
				{labels.map((label) => {
					const count = counts[label.id] || 0
					if (count === 0) return null
					return (
						<button
							key={label.id}
							className="label-filter-pill"
							data-active={activeFilter === label.id}
							aria-pressed={activeFilter === label.id}
							style={{ '--label-color': label.color } as React.CSSProperties}
							onClick={() => onFilterChange(label.id)}
							draggable
							onDragStart={() => handleDragStart(label.id)}
							onDragOver={(e) => handleDragOver(e, label.id)}
							onDragEnd={handleDragEnd}
						>
							<span
								className="label-filter-pill-dot"
								style={{ backgroundColor: label.color }}
							/>
							{label.name}
							<span className="label-filter-pill-count">{count}</span>
						</button>
					)
				})}

				{/* Untagged pill */}
				{showUntagged && (
					<button
						className="label-filter-pill label-filter-pill-untagged"
						data-active={activeFilter === UNTAGGED_FILTER}
						aria-pressed={activeFilter === UNTAGGED_FILTER}
						onClick={() => onFilterChange(UNTAGGED_FILTER)}
					>
						Untagged
						<span className="label-filter-pill-count">{untaggedCount}</span>
					</button>
				)}

				{/* Filter button (multi-select) */}
				{labels.length > 0 && (
					<button
						ref={filterBtnRef}
						className="label-filter-pill label-filter-pill-action"
						data-active={isMultiFilter}
						aria-pressed={isMultiFilter}
						onClick={() => setShowFilter(!showFilter)}
						aria-label="Filter by multiple labels"
					>
						Filter
						{isMultiFilter && (
							<span className="label-filter-pill-count">{activeFilterIds.length}</span>
						)}
					</button>
				)}

				{/* Create button */}
				<button
					ref={createBtnRef}
					className="label-filter-pill label-filter-pill-action"
					onClick={() => setShowCreate(!showCreate)}
					aria-label="Create label"
				>
					+
				</button>
			</div>

			{/* Popovers */}
			{showCreate && (
				<LabelCreatePopover
					anchorRef={createBtnRef}
					existingColors={labels.map((l) => l.color)}
					onCreateLabel={async (data) => {
						await onCreateLabel(data)
						setShowCreate(false)
					}}
					onClose={handleCloseCreate}
				/>
			)}

			{showFilter && onFilterIdsChange && (
				<LabelFilterPopover
					anchorRef={filterBtnRef}
					labels={labels}
					counts={counts}
					selectedIds={activeFilterIds}
					onSelectionChange={onFilterIdsChange}
					onClose={handleCloseFilter}
				/>
			)}
		</div>
	)
}
